import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtNum, METHODS } from "@/lib/api";
import { Empty, NativeSelect, Spinner } from "@/components/Brand";
import { Confirm } from "@/components/Confirm";
import { GoldBtn, PageTitle } from "@/pages/admin/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const BLANK = { name: "", code: "", description: "", active: true, scoring_method: "benar_salah", score_correct: 5, score_wrong: 0, score_blank: 0, passing_grade: "", default_question_count: 10, duration_minutes: 0, weight: 1, order: 0 };
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const SubjectForm = ({ f, setF, cats, onSave }) => (
  <form onSubmit={onSave} className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Label>Kategori</Label><NativeSelect required value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="subject-category-select">{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect></div>
      <div><Label>Nama materi</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="subject-name-input" /></div>
      <div><Label>Kode singkat</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="mis. TWK" data-testid="subject-code-input" /></div>
    </div>
    <div><Label>Deskripsi</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
    <div><Label>Metode penilaian</Label>
      <NativeSelect value={f.scoring_method} onChange={(e) => setF({ ...f, scoring_method: e.target.value })} data-testid="subject-method-select">
        <option value="benar_salah">Benar/Salah (kunci jawaban)</option><option value="skor_pilihan">Skor per pilihan jawaban (mis. TKP)</option>
      </NativeSelect>
    </div>
    <div className="grid grid-cols-3 gap-3">
      {f.scoring_method === "benar_salah" && <>
        <div><Label>Skor benar</Label><Input type="number" step="any" value={f.score_correct} onChange={(e) => setF({ ...f, score_correct: e.target.value })} data-testid="subject-score-correct" /></div>
        <div><Label>Skor salah</Label><Input type="number" step="any" value={f.score_wrong} onChange={(e) => setF({ ...f, score_wrong: e.target.value })} data-testid="subject-score-wrong" /></div>
      </>}
      <div><Label>Skor kosong</Label><Input type="number" step="any" value={f.score_blank} onChange={(e) => setF({ ...f, score_blank: e.target.value })} data-testid="subject-score-blank" /></div>
      <div><Label>Passing grade</Label><Input type="number" step="any" value={f.passing_grade ?? ""} placeholder="kosong = tidak ada" onChange={(e) => setF({ ...f, passing_grade: e.target.value })} data-testid="subject-pg-input" /></div>
      <div><Label>Bobot</Label><Input type="number" step="any" min="0" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} data-testid="subject-weight-input" /></div>
      <div><Label>Jumlah soal target</Label><Input type="number" min="0" value={f.default_question_count} onChange={(e) => setF({ ...f, default_question_count: e.target.value })} data-testid="subject-qcount-input" /></div>
      <div><Label>Durasi saran (menit)</Label><Input type="number" min="0" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} data-testid="subject-duration-input" /></div>
    </div>
    <p className="text-xs text-slate-500">Skor maksimal dihitung otomatis: Benar/Salah = jumlah soal × skor benar; Skor per pilihan = jumlah skor tertinggi tiap soal. Durasi ujian yang berlaku diatur di paket tryout.</p>
    <div className="flex items-center gap-3"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="subject-active-switch" /><Label>Aktif</Label></div>
    <GoldBtn type="submit" className="w-full justify-center py-2.5" data-testid="subject-save-btn">Simpan</GoldBtn>
  </form>
);

export default function SubjectsAdmin() {
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [rows, setRows] = useState(null);
  const [f, setF] = useState(null);

  useEffect(() => {
    api.get("/categories", { params: { all: true } }).then((r) => { setCats(r.data); if (r.data[0]) setCat(r.data[0].id); });
  }, []);
  const load = useCallback(() => {
    if (!cat) return;
    api.get("/subjects", { params: { category_id: cat, all: true } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [cat]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, passing_grade: num(f.passing_grade), score_correct: Number(f.score_correct), score_wrong: Number(f.score_wrong), score_blank: Number(f.score_blank),
      weight: Number(f.weight), default_question_count: Number(f.default_question_count), duration_minutes: Number(f.duration_minutes), order: Number(f.order) || 0 };
    try {
      if (f.id) await api.put(`/admin/subjects/${f.id}`, body);
      else await api.post("/admin/subjects", body);
      toast.success("Materi disimpan");
      setF(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const move = async (i, d) => {
    const ids = rows.map((r) => r.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try {
      await api.put("/admin/subjects-reorder", { ids });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = async (s) => {
    try {
      await api.patch(`/admin/subjects/${s.id}`, { active: !s.active });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (s) => {
    try {
      await api.delete(`/admin/subjects/${s.id}`);
      toast.success("Materi dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageTitle title="Manajemen Materi" desc="Atur materi, metode penilaian, skor, passing grade, bobot, dan urutan tampil.">
        <GoldBtn onClick={() => setF({ ...BLANK, category_id: cat })} disabled={!cat} data-testid="subject-add-btn"><Plus className="h-4 w-4" />Tambah Materi</GoldBtn>
      </PageTitle>
      <div className="mb-4 max-w-xs"><NativeSelect value={cat} onChange={(e) => setCat(e.target.value)} data-testid="subjects-category-filter">{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect></div>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty text="Belum ada materi di kategori ini." /> : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full min-w-[820px] text-sm" data-testid="subjects-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Urut</th><th>Materi</th><th>Metode</th><th>Skor B/S/K</th><th>Passing grade</th><th>Bobot</th><th>Soal di bank</th><th>Aktif</th><th className="pr-4 text-right">Aksi</th></tr></thead>
            <tbody>{rows.map((s, i) => (
              <tr key={s.id} className="border-t" data-testid={`subject-row-${s.code || s.name}`}>
                <td className="px-4 py-3"><span className="flex gap-1">
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-30" data-testid={`subject-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                  <button disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="disabled:opacity-30" data-testid={`subject-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                </span></td>
                <td className="font-semibold">{s.code && <span className="mr-1 text-amber-700">{s.code}</span>}{s.name}</td>
                <td>{METHODS[s.scoring_method]}</td>
                <td>{s.scoring_method === "benar_salah" ? `${s.score_correct} / ${s.score_wrong} / ${s.score_blank}` : `per pilihan / ${s.score_blank}`}</td>
                <td>{fmtNum(s.passing_grade)}</td><td>{s.weight}</td><td>{s.question_count}</td>
                <td><Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`subject-toggle-${i}`} /></td>
                <td className="pr-4 text-right">
                  <button onClick={() => setF({ ...s, passing_grade: s.passing_grade ?? "" })} className="mr-2" data-testid={`subject-edit-${i}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                  <Confirm testid={`subject-delete-${i}`} title="Hapus materi?" description="Seluruh soal dalam materi ini ikut terhapus. Materi yang dipakai paket tidak dapat dihapus." onConfirm={() => del(s)}
                    trigger={<button data-testid={`subject-delete-btn-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>} />
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="subject-dialog">
          <DialogHeader><DialogTitle>{f?.id ? "Edit materi" : "Tambah materi"}</DialogTitle></DialogHeader>
          {f && <SubjectForm f={f} setF={setF} cats={cats} onSave={save} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
